// useWeeklyFeature — TanStack Query wrapper around the
// get-weekly-candidates Edge Function. Reads strictly via the Edge
// Function — never touches `scans` directly for FotW data, so the
// anonymity boundary lives in one place.

/**
 * Resolve the PT Monday for a given date. Used by the Home card to
 * request the previous week's winner. SQL has fotw_pt_monday() as the
 * authoritative version; this client-side helper just needs to match
 * the date boundary closely enough for the lookup to find the right
 * row (off-by-one-day mismatches near midnight DST swaps are rare and
 * acceptable — the worst case is "no winner card for a few hours").
 */
export function getPtMondayISO(date: Date = new Date()): string {
  // Pull the calendar date as observed in PT.
  const ymd = date.toLocaleDateString("en-CA", {
    timeZone: "America/Los_Angeles",
  }); // "YYYY-MM-DD"
  const d = new Date(ymd + "T00:00:00");
  const dow = d.getDay(); // 0=Sun..6=Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

export function getPrevPtMondayISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return getPtMondayISO(d);
}

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface FotwCandidateOpen {
  scan_id: string;
  photos: string[];
  verdict_label: string;
  brand: string | null;
  category: string | null;
  era: string | null;
}

export interface FotwCandidateRevealed extends FotwCandidateOpen {
  user_id: string;
  handle: string;
  avatar: string;
  is_winner: boolean;
}

export interface FotwResponse {
  week_start: string;
  finalized: boolean;
  skipped: boolean;
  winning_scan_id?: string;
  candidates: Array<FotwCandidateOpen | FotwCandidateRevealed>;
  my_vote: string | null;
}

export function useWeeklyFeature(weekStart?: string) {
  return useQuery({
    queryKey: ["weekly-feature", weekStart ?? "current"],
    staleTime: 30_000,
    queryFn: async (): Promise<FotwResponse> => {
      const { data, error } = await supabase.functions.invoke<FotwResponse>(
        "get-weekly-candidates",
        { body: weekStart ? { week_start: weekStart } : {} },
      );
      if (error) throw error;
      // The Edge Function always returns a body even when there's no row.
      return (data as FotwResponse) ?? {
        week_start: weekStart ?? "",
        finalized: false,
        skipped: false,
        candidates: [],
        my_vote: null,
      };
    },
  });
}

export function useCastWeeklyVote(weekStart: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (scanId: string) => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        voted_for: string;
      }>("cast-weekly-vote", {
        body: { week_start: weekStart, scan_id: scanId },
      });
      if (error) throw error;
      return data!;
    },
    onSuccess: () => {
      // Refetch so my_vote reflects the new pick.
      void qc.invalidateQueries({ queryKey: ["weekly-feature"] });
    },
  });
}
